/**
 * This page is loaded by Nextjs:
 *  - on the server, when data-fetching methods throw or reject
 *  - on the client, when `getInitialProps` throws or rejects
 *  - on the client, when a React lifecycle method throws or rejects, and it's
 *    caught by the built-in Nextjs error boundary
 *
 * See:
 *  - https://nextjs.org/docs/basic-features/data-fetching/overview
 *  - https://nextjs.org/docs/api-reference/data-fetching/get-initial-props
 *  - https://reactjs.org/docs/error-boundaries.html
 */

import { NextPageContext } from "next";
import NextErrorComponent from "next/error";
import * as Sentry from "@sentry/nextjs";
import { getClientAppInsights } from "@modules/core/applicationInsights/clientTelemetryService";

type AppErrorProps = {
  statusCode: number;
};

const ErrorPage = ({ statusCode }: AppErrorProps) => {
  return <NextErrorComponent statusCode={statusCode} />;
};

const logToAppInsights = (props: NextPageContext) => {
  const { err, asPath } = props;
  const { clientAppInsights, initialize: initializeAppInsights } =
    getClientAppInsights();

  // Running on the server, the response object (`res`) is available.
  //
  // Next.js will pass an err on the server if a page's data fetching methods
  // threw or returned a Promise that rejected
  //
  // Running on the client (browser), Next.js will provide an err if:
  //
  //  - a page's `getInitialProps` threw or returned a Promise that rejected
  //  - an exception was thrown somewhere in the React lifecycle (render,
  //    componentDidMount, etc) that was caught by Next.js's React Error
  //    Boundary. Read more about what types of exceptions are caught by Error
  //    Boundaries: https://reactjs.org/docs/error-boundaries.html

  if (!clientAppInsights) {
    initializeAppInsights();
  }

  if (err) {
    clientAppInsights?.trackException({
      exception: err,
    });

    return;
  }

  // If this point is reached, getInitialProps was called without any
  // information about what the error might be. This is unexpected and may
  // indicate a bug introduced in Next.js, so record it in Application Insights
  clientAppInsights?.trackException({
    exception: new Error(
      `_error.js getInitialProps missing data at path: ${asPath}`
    ),
  });

  clientAppInsights?.flush();
};

ErrorPage.getInitialProps = async (props: NextPageContext) => {
  await Sentry.captureUnderscoreErrorException(props);
  logToAppInsights(props);

  // This will contain the status code of the response
  return NextErrorComponent.getInitialProps(props);
};

ErrorPage.pageName = "ErrorPage";

export default ErrorPage;
